/* LocalVeilGLRenderer.js */

"use strict";
/* jshint ignore:start */
const _ = require("lodash");

class LocalVeilGLRenderer {
    /**
     * @member rootElement {HTMLElement} - Get the root element of the renderer.
     * @member model {String|HTMLElement} - Get the model associated with this renderer.
     */

    /**
     * Create a renderer for the veil.
     * @param model {LocalVeilGLComponent} - The model that is bound to the renderer.
     */
    constructor(model) {
        this._model = model;
        this._transitioning = false;
        this._rootElem = document.createElement("local-veil-gl");
        this._rootElem.component = this._model;
        this._titleContainer = document.createElement("h3");
        this._closeButton = document.createElement("actionable-icon-gl");
        this._closeButton.classList.add("close-btn");
        this._closeButton.dataset.icon = "common.close";
        this._closeButton.dataset.darkTheme = true;
        this._contentContainer = document.createElement("content-container");
        this._rootElem.appendChild(this._titleContainer);
        this._rootElem.appendChild(this._closeButton);
        this._rootElem.appendChild(this._contentContainer);
        this._windowKeyHandler = this._windowKeyHandler.bind(this);
        this._registerListeners();
        this._showPromise = undefined;
        this._hidePromise = undefined;
        this._rootElem.style.transitionDuration = `${this._model._ANIMATION_DURATION}ms`;
        this._rootElem.style.transitionTimingFunction = "cubic-bezier(0.86, 0, 0.07, 1)"; //easeInOutQuint
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * The shutdown promise used to kill the DOM and all members.
     * @returns {Promise}
     * @private
     */
    shutdown() {
        this._unregisterListeners();
        let prom = Promise.resolve();

        if (this.getHidePromise()) {
            prom = prom.then(this.getHidePromise());
        }
        if (this.getShowPromise()) {
            prom = prom.then(this.getShowPromise());
        }
        return prom.then(() => {
            this._titleContainer.remove();
            this._titleContainer = undefined;
            this._closeButton.remove();
            this._closeButton = undefined;
            this._contentContainer.remove();
            this._contentContainer = undefined;
            this._model = undefined;
            this._transitioning = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._showPromise = undefined;
            this._hidePromise = undefined;
        });
    }

    /**
     * Register any listeners used in the renderer.
     * @private
     */
    _registerListeners() {
        this._model.on("UpdateTitle", (this._onTitleChange = this._onTitleChange.bind(this)));
        this._model.on("UpdateContent", (this._onContentChange = this._onContentChange.bind(this)));
        this._model.on(
            "UpdateUnclosableState",
            (this._onUnclosableChange = this._onUnclosableChange.bind(this))
        );
        this._model.on(
            "UpdateTransparentState",
            (this._onTransparentChange = this._onTransparentChange.bind(this))
        );
        this._model.on(
            "UpdateDarkTheme",
            (this._onDarkThemeChange = this._onDarkThemeChange.bind(this))
        );
        this._model.on(
            "UpdateTopPosition",
            (this._onTopPositionChange = this._onTopPositionChange.bind(this))
        );
        this._closeButton.addEventListener(
            "click",
            (this._onCloseButtonClick = this._onCloseButtonClick.bind(this))
        );
        this._closeButton.addEventListener(
            "mousedown",
            (this._onCloseButtonMouseDown = this._onCloseButtonMouseDown.bind(this))
        );
        this._closeButton.addEventListener(
            "mouseleave",
            (this._onCloseButtonMouseLeave = this._onCloseButtonMouseLeave.bind(this))
        );
        this._closeButton.addEventListener(
            "mouseup",
            (this._onCloseButtonMouseUp = this._onCloseButtonMouseUp.bind(this))
        );
    }

    /**
     * Unregister any listeners used inside of the renderer.
     * @private
     */
    _unregisterListeners() {
        this._model.removeListener("UpdateTitle", this._onTitleChange);
        this._model.removeListener("UpdateContent", this._onContentChange);
        this._model.removeListener("UpdateUnclosableState", this._onUnclosableChange);
        this._model.removeListener("UpdateTransparentState", this._onTransparentChange);
        this._model.removeListener("UpdateDarkTheme", this._onDarkThemeChange);
        this._model.removeListener("UpdateTopPosition", this._onTopPositionChange);
        this._closeButton.removeEventListener("click", this._onCloseButtonClick);
        this._closeButton.removeEventListener("mousedown", this._onCloseButtonMouseDown);
        this._closeButton.removeEventListener("mouseleave", this._onCloseButtonMouseLeave);
        this._closeButton.removeEventListener("mouseup", this._onCloseButtonMouseUp);
        window.removeEventListener("keydown", this._windowKeyHandler);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise}
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._updateTitle(this._model.title);
            this._updateContent(this._model.content);
            this._updateUnclosableState(this._model.unclosable);
            this._updateTransparentState(this._model.transparent);
            this._updateDarkTheme(this._model.darkTheme);
            if (this.model.shown === true) {
                resolve(this.show());
            } else {
                resolve();
            }
        });
        return prom;
    }

    /**
     * Returns the root element of the renderer
     * @returns {HTMLElement}
     */
    getRootElement() {
        return this._rootElem;
    }
    get rootElement() {
        return this.getRootElement();
    }

    /**
     * Returns the model associated with this renderer.
     * @returns {LocalVeilGLComponent}
     */
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Event handler generated by the model providing a new title
     * @param newTitle {String|HTMLElement}
     * @private
     */
    _onTitleChange(newTitle) {
        this._updateTitle(newTitle);
    }

    /**
     * Event handler generated by the model providing new content
     * @param newContent {String|HTMLElement}
     * @private
     */
    _onContentChange(newContent) {
        this._updateContent(newContent);
    }

    /**
     * Event handler generated by the model to animate the DOM into view
     * @private
     */
    _onShow() {
        this._show();
    }

    /**
     * Event handler generated by the model to animate the DOM out of view
     * @private
     */
    _onHide() {
        this._hide();
    }

    /**
     * Event handler generated by the model to update the unclosable state
     * @param newState {Boolean}
     * @private
     */
    _onUnclosableChange(newState) {
        this._updateUnclosableState(newState);
    }

    /**
     * Event handler generated by the model to update the transparent state
     * @param newState {Boolean}
     * @private
     */
    _onTransparentChange(newState) {
        this._updateTransparentState(newState);
    }

    /**
     * Event handler generated by the model to toggle on/off the dark theme
     * @param newState {Boolean}
     * @private
     */
    _onDarkThemeChange(newState) {
        this._updateDarkTheme(newState);
    }

    /**
     * Event handler generated by the model to update the top position of the veil
     * @param newState {Number}
     * @private
     */
    _onTopPositionChange(newState) {
        this._updateTopPosition(newState);
    }

    /**
     * Event handler for the click event of the close button
     * @param event {Event}
     * @private
     */
    _onCloseButtonClick(/*event*/) {
        this._triggerUserHide();
        this._model.hide().then(() => {
            this._triggerUserHideComplete();
        });
    }

    /**
     * Event handler for the mousedown event of the close button
     * @param event {Event}
     * @private
     */
    _onCloseButtonMouseDown(/*event*/) {
        this._closeButton.classList.add("active");
    }

    /**
     * Event handler for the mouseup event of the close button
     * @param event {Event}
     * @private
     */
    _onCloseButtonMouseUp(/*event*/) {
        this._closeButton.classList.remove("active");
    }

    /**
     * Event handler for the mouseleave event of the close button
     * @param event {Event}
     * @private
     */
    _onCloseButtonMouseLeave(/*event*/) {
        this._closeButton.classList.remove("active");
    }

    /**
     * Event handler for the keydown event of the window object
     * @param event {Event}
     * @private
     */
    _windowKeyHandler(event) {
        if (event.keyCode === 27 && !this._model.unclosable) {
            this._triggerUserHide();
            this._model.hide().then(() => {
                this._triggerUserHideComplete();
            });
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Sets the passed in content for the passed in DOM container.
     * @param content {String|HTMLElement}
     * @param container {HTMLElement}
     * @private
     */
    _updateDOMContent(content, container) {
        container.innerHTML = "";
        if (typeof content === "string") {
            container.innerHTML = content;
        } else if (typeof content === "object" && content !== null) {
            if (content instanceof HTMLElement || content instanceof DocumentFragment) {
                container.appendChild(content);
            } else if (!_.isUndefined(content.renderer) && !_.isUndefined(content.shutdown)) {
                container.appendChild(content.renderer.getRootElement());
            }
        }
    }

    /**
     * Sets the visual title.
     * @param newTitle {String|HTMLElement}
     * @private
     */
    _updateTitle(newTitle) {
        this._updateDOMContent(newTitle, this._titleContainer);
    }

    /**
     * Sets the inner content.
     * @param newContent {String|HTMLElement}
     * @private
     */
    _updateContent(newContent) {
        this._updateDOMContent(newContent, this._contentContainer);
    }

    /**
     * Run animation based on CSS transition property
     * @param topStart {String} - starting top property value
     * @param topEnd {String} - ending top property value
     * @private
     */
    _runTransition(topStart, topEnd) {
        this._rootElem.style.top = topStart;
        this._rootElem.style.transitionProperty = "top";
        this._rootElem.style.top = topEnd;

        this._transitioning = true;
        return new Promise((resolve) => {
            setTimeout(() => {
                if (this._rootElem) {
                    this._rootElem.style.transitionProperty = "";
                }
                this._transitioning = false;
                resolve();
            }, this._model._ANIMATION_DURATION);
        });
    }

    /**
     * Updates the unclosable state
     * @param newState {Boolean}
     * @private
     */
    _updateUnclosableState(newState) {
        if (newState === true) {
            this._rootElem.classList.add("unclosable");
        } else {
            this._rootElem.classList.remove("unclosable");
        }
    }

    /**
     * Updates the transparent state
     * @param newState {Boolean}
     * @private
     */
    _updateTransparentState(newState) {
        if (newState === true) {
            this._rootElem.classList.remove("opaque");
        } else {
            this._rootElem.classList.add("opaque");
        }
    }

    /**
     * Toggles on/off the dark theme
     * @param newState {Boolean}
     * @private
     */
    _updateDarkTheme(newState) {
        if (newState === true) {
            this._rootElem.classList.add("dark");
            this._closeButton.setDarkTheme(true);
        } else {
            this._rootElem.classList.remove("dark");
            this._closeButton.setDarkTheme(false);
        }
    }

    /**
     * Updates the top position of the veil, if shown
     * @param newState {Number}
     * @private
     */
    _updateTopPosition(newState) {
        if (this._model.isShown()) {
            this._rootElem.style.top = newState + "rem";
        }
    }

    /**
     * Triggers the 'UserHide' and 'UserHideComplete' events when the veil is hidden
     * through user interaction
     * @private
     */
    _triggerUserHideComplete() {
        this._model.emit("UserHideComplete");
    }
    _triggerUserHide() {
        this._model.emit("UserHide");
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    //These methods return promises and control when completed therefore are a special case public Renderer API.
    getShowPromise() {
        return this._showPromise;
    }
    getHidePromise() {
        return this._hidePromise;
    }
    /**
     * Animates the veil into view.
     * @private
     */
    show() {
        let chain = Promise.resolve();
        if (this.model.shown) {
            return chain;
        }
        if (this.getHidePromise()) {
            chain = chain.then(() => {
                return this.getHidePromise();
            });
        }
        chain = chain.then(() => {
            this.model._shown = true;
            this._showPromise = new Promise((resolve) => {
                this._model._shown = true;
                let offsetHeight =
                    this._rootElem.parentElement && this._rootElem.parentElement.offsetHeight > 0
                        ? this._rootElem.parentElement.offsetHeight
                        : document.body.offsetHeight;
                let topOffset = Math.ceil(((this._model.topPosition * 10) / offsetHeight) * 100);

                this._runTransition("100%", topOffset + "px").then(() => {
                    this._rootElem.style.top = this._model.topPosition + "rem";
                    window.addEventListener("keydown", this._windowKeyHandler);
                    resolve();
                });
            });
            return this._showPromise;
        });
        return chain;
    }

    /**
     * Animates the veil out of view.
     * @private
     */
    hide() {
        let chain = Promise.resolve();
        if (!this.model.shown) {
            return chain;
        }
        if (this.getShowPromise()) {
            chain = chain.then(() => {
                return this.getShowPromise();
            });
        }
        chain = chain.then(() => {
            this.model._shown = false;
            this._hidePromise = new Promise((resolve) => {
                this._model._shown = false;
                window.removeEventListener("keydown", this._windowKeyHandler);
                let offsetHeight =
                    this._rootElem.parentElement && this._rootElem.parentElement.offsetHeight > 0
                        ? this._rootElem.parentElement.offsetHeight
                        : document.body.offsetHeight;
                let topOffset = ((this._model.topPosition * 10) / offsetHeight) * 100;

                this._runTransition(topOffset + "px", "100%").then(resolve);
            });
            return this._hidePromise;
        });
        return chain;
    }
    /*******************************************/
    /*  END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = LocalVeilGLRenderer;
/* jshint ignore:end */
